// MealConsumed.js - Fixed with all imports
import React from "react";
import {
  Box,
  Typography,
  Chip,
  alpha,
  LinearProgress,
  Stack,
  Paper,
  Slider,
} from "@mui/material";
import {
  LocalFireDepartment as LocalFireDepartmentIcon,
  EmojiEvents as EmojiEventsIcon,
  SentimentSatisfiedAlt as SentimentSatisfiedAltIcon,
  SentimentDissatisfied as SentimentDissatisfiedIcon,
  SentimentVeryDissatisfied as SentimentVeryDissatisfiedIcon,
} from "@mui/icons-material";

const getPercentageColor = (percentage) => {
  if (percentage >= 80) return "#22C55E";
  if (percentage >= 60) return "#3B82F6";
  if (percentage >= 40) return "#F59E0B";
  if (percentage >= 20) return "#F97316";
  return "#EF4444";
};

const getPercentageLabel = (percentage) => {
  if (percentage >= 80) return "Excellent";
  if (percentage >= 60) return "Good";
  if (percentage >= 40) return "Average";
  if (percentage >= 20) return "Poor";
  return "Very Poor";
};

const getPercentageIcon = (percentage) => {
  if (percentage >= 80) return <EmojiEventsIcon fontSize="small" />;
  if (percentage >= 60) return <SentimentSatisfiedAltIcon fontSize="small" />;
  if (percentage >= 40) return <SentimentDissatisfiedIcon fontSize="small" />;
  return <SentimentVeryDissatisfiedIcon fontSize="small" />;
};

const percentageMarks = [
  { value: 0, label: "0%" },
  { value: 25, label: "25%" },
  { value: 50, label: "50%" },
  { value: 75, label: "75%" },
  { value: 100, label: "100%" },
];

const MealConsumed = ({ value = 0, onChange }) => {
  const percentage = value || 0;
  const color = getPercentageColor(percentage);

  const handleChange = (event, newValue) => {
    if (onChange) {
      onChange(event, newValue);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        bgcolor: "#F8FAFC",
        borderRadius: "8px",
        border: "1px solid #E2E8F0",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocalFireDepartmentIcon
              sx={{ color: color, fontSize: 20 }}
            />
            <Typography variant="subtitle2" fontWeight={600} color="#1A1A2E">
              Food Consumed
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {getPercentageIcon(percentage)}
            <Typography
              variant="h6"
              fontWeight={700}
              color={color}
            >
              {percentage}%
            </Typography>
          </Box>
        </Box>

        <Slider
          value={percentage}
          onChange={handleChange}
          step={5}
          marks={percentageMarks}
          min={0}
          max={100}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => `${value}%`}
          sx={{
            color: color,
            height: 8,
            "& .MuiSlider-track": {
              height: 8,
              borderRadius: 4,
              transition: "all 0.3s ease",
            },
            "& .MuiSlider-rail": {
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(color, 0.12),
            },
            "& .MuiSlider-thumb": {
              width: 20,
              height: 20,
              bgcolor: "white",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              "&:hover, &.Mui-active": {
                boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
              },
            },
            "& .MuiSlider-mark": {
              bgcolor: "#98A2B3",
              height: 4,
              width: 2,
            },
            "& .MuiSlider-markLabel": {
              fontSize: "0.6rem",
              fontWeight: 500,
              color: "#98A2B3",
            },
          }}
        />

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="caption" color="#98A2B3">
            {percentage === 0
              ? "Move the slider to record how much was consumed"
              : `${getPercentageLabel(percentage)} - ${percentage}% consumed`}
          </Typography>
          <Chip
            label={getPercentageLabel(percentage)}
            size="small"
            sx={{
              height: 24,
              fontSize: "0.65rem",
              fontWeight: 600,
              bgcolor: alpha(color, 0.12),
              color: color,
              "& .MuiChip-label": { px: 1.5 },
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default MealConsumed;